import React from 'react';
import { Check } from 'lucide-react';

/**
 * Generic workflow step indicator component
 *
 * @param {Object} props
 * @param {Array<{num: number, label: string}>} props.steps - Array of step definitions
 * @param {number} props.currentStep - Current active step number
 * @param {string} [props.accentColor='amber'] - Tailwind color for active step (amber, blue, red, etc.)
 * @returns {JSX.Element}
 */
function WorkflowStepIndicator({ steps, currentStep, accentColor = 'amber' }) {
  const activeClasses = {
    amber: 'bg-amber-500 text-white',
    blue: 'bg-blue-500 text-white',
    red: 'bg-red-500 text-white',
    green: 'bg-green-500 text-white',
    purple: 'bg-purple-500 text-white',
  };

  const activeLabelClasses = {
    amber: 'text-amber-600',
    blue: 'text-blue-600',
    red: 'text-red-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
  };

  const activeClass = activeClasses[accentColor] || activeClasses.amber;
  const activeLabelClass = activeLabelClasses[accentColor] || activeLabelClasses.amber;

  return (
    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        {steps.map((step, idx) => (
          <div key={step.num} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                currentStep === step.num
                  ? activeClass
                  : currentStep > step.num
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {currentStep > step.num ? <Check className="w-4 h-4" /> : step.num}
              </div>
              <span className={`mt-1 text-[10px] font-medium text-center ${
                currentStep === step.num ? activeLabelClass : 'text-gray-600'
              }`}>
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`h-0.5 flex-1 mx-1 ${
                currentStep > step.num ? 'bg-green-500' : 'bg-gray-300'
              }`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default WorkflowStepIndicator;
